import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FEATURE_KEYS,
  FEATURE_LABELS,
  LIMIT_KEYS,
  LIMIT_LABELS,
  MODULE_CODES,
  MODULE_LABELS,
  type ModuleCode,
  type PlanInput,
  type PlatformPlanDetail,
} from '@azko/shared';

import { ApiRequestError, api } from '../../lib/api.js';
import { formatCurrency } from '../../lib/format.js';
import { useSession } from '../../lib/session.js';
import {
  Alert,
  Button,
  Card,
  Field,
  LoadingRows,
  StatusBadge,
  TextInput,
  cx,
} from '../../components/ui.js';

/**
 * O catalogo como a Azko o ve — incluindo os planos fechados, que somem da
 * vitrine do cliente mas continuam valendo para quem ja assina.
 *
 * Ate agora era somente leitura: `plataforma:planos:criar` e `:editar` estavam
 * na matriz e nao tinham rota, entao criar plano ou mexer num preco era SQL na
 * mao — no produto cujo negocio E vender plano.
 *
 * A coluna que importa continua sendo a de assinantes: e ela que diz se mexer
 * num plano e ajuste ou incidente. Preco, limite e modulo sao lidos do plano a
 * cada request, nao copiados para a assinatura — entao salvar aqui muda o que
 * aquelas contas pagam e podem fazer, na hora. O formulario avisa isso quando
 * ha alguem no plano.
 *
 * NAO HA EXCLUIR, e nao e esquecimento: `subscriptions` aponta para o plano.
 * Despublicar (`is_public = 0`) e a operacao que existe de verdade — tira da
 * vitrine e preserva quem ja assina.
 */

const EMPTY_PLAN: PlanInput = {
  code: '',
  name: '',
  description: '',
  priceCents: 0,
  billingPeriod: 'monthly',
  trialDays: 14,
  sortOrder: 0,
  isPublic: true,
  modules: ['core'],
  limits: {},
  features: {},
};

function toInput(plan: PlatformPlanDetail): PlanInput {
  return {
    name: plan.name,
    description: plan.description,
    priceCents: plan.priceCents,
    billingPeriod: plan.billingPeriod,
    trialDays: plan.trialDays,
    sortOrder: plan.sortOrder,
    isPublic: plan.isPublic,
    modules: plan.modules as ModuleCode[],
    limits: plan.limits,
    features: plan.features,
  };
}

export function PlansPage() {
  const queryClient = useQueryClient();
  const { staff } = useSession();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlanInput>(EMPTY_PLAN);
  const [error, setError] = useState<string | null>(null);

  const podeCriar = staff?.permissions.includes('plataforma:planos:criar') ?? false;
  const podeEditar = staff?.permissions.includes('plataforma:planos:editar') ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'planos'],
    queryFn: () => api.get<{ items: PlatformPlanDetail[] }>('/admin/planos'),
  });

  const items = data?.items ?? [];
  const totalContas = items.reduce((sum, plan) => sum + plan.subscribers, 0);
  const criando = editing === '__novo__';
  const planoEmEdicao = items.find((plan) => plan.code === editing);

  function close() {
    setEditing(null);
    setError(null);
  }

  const save = useMutation({
    mutationFn: () =>
      criando
        ? api.post<PlatformPlanDetail>('/admin/planos', draft)
        : api.put<PlatformPlanDetail>(`/admin/planos/${editing ?? ''}`, draft),
    onSuccess: () => {
      close();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'planos'] });
    },
    onError: (cause) =>
      setError(cause instanceof ApiRequestError ? cause.message : 'Nao foi possivel salvar.'),
  });

  function openNew() {
    setDraft(EMPTY_PLAN);
    setEditing('__novo__');
    setError(null);
  }

  function openEdit(plan: PlatformPlanDetail) {
    setDraft(toInput(plan));
    setEditing(plan.code);
    setError(null);
  }

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Plataforma</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight">Planos.</h1>
          <p className="mt-2 text-sm text-muted">
            {items.length} no catalogo • {totalContas}{' '}
            {totalContas === 1 ? 'conta assinante' : 'contas assinantes'}
          </p>
        </div>
        {podeCriar && !editing && <Button onClick={openNew}>Novo plano</Button>}
      </div>

      {editing && (
        <Card className="mt-4 p-5 sm:p-6">
          <h2 className="text-xl font-bold tracking-tight">
            {criando ? 'Novo plano' : `Editando ${planoEmEdicao?.name ?? editing}`}
          </h2>

          {/* O aviso so aparece quando ha alguem para ser afetado — texto de
              risco que aparece sempre vira ruido e deixa de ser lido. */}
          {!criando && (planoEmEdicao?.subscribers ?? 0) > 0 && (
            <div className="mt-4">
              <Alert tone="warn">
                {planoEmEdicao?.subscribers}{' '}
                {planoEmEdicao?.subscribers === 1 ? 'conta usa' : 'contas usam'} este plano. Preco,
                limites e modulos valem na hora em que voce salvar — inclusive para elas.
              </Alert>
            </div>
          )}

          {error && (
            <div className="mt-4">
              <Alert>{error}</Alert>
            </div>
          )}

          <form
            className="mt-5 grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              save.mutate();
            }}
          >
            {criando && (
              <Field label="Codigo" className="sm:col-span-2">
                <TextInput
                  value={draft.code ?? ''}
                  onChange={(event) =>
                    setDraft({ ...draft, code: event.target.value.toLowerCase() })
                  }
                  placeholder="portaria24h"
                  maxLength={32}
                />
                {/* Dito na criacao porque depois nao ha o que fazer: o codigo e
                    a chave que assinatura, landing e onboarding citam. */}
                <span className="text-xs text-faint">
                  Nao muda depois. E o identificador que a assinatura e o site usam.
                </span>
              </Field>
            )}

            <Field label="Nome">
              <TextInput
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                maxLength={80}
              />
            </Field>

            <Field label="Preco (R$)">
              <TextInput
                value={(draft.priceCents / 100).toString()}
                onChange={(event) => {
                  // Guardado em centavos: dinheiro em float acumula erro de
                  // arredondamento, e aqui ele viraria cobranca.
                  const parsed = Number(event.target.value.replace(',', '.'));
                  setDraft({
                    ...draft,
                    priceCents: Number.isFinite(parsed) ? Math.round(parsed * 100) : 0,
                  });
                }}
                inputMode="decimal"
              />
            </Field>

            <Field label="Descricao" className="sm:col-span-2">
              <TextInput
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
                maxLength={255}
              />
            </Field>

            <Field label="Dias de teste">
              <TextInput
                value={String(draft.trialDays)}
                onChange={(event) =>
                  setDraft({ ...draft, trialDays: Number(event.target.value.replace(/\D/g, '')) })
                }
                inputMode="numeric"
              />
            </Field>

            <Field label="Ordem na vitrine">
              <TextInput
                value={String(draft.sortOrder)}
                onChange={(event) =>
                  setDraft({ ...draft, sortOrder: Number(event.target.value.replace(/\D/g, '')) })
                }
                inputMode="numeric"
              />
            </Field>

            <label className="flex items-center gap-2 text-sm text-muted sm:col-span-2">
              <input
                type="checkbox"
                checked={draft.isPublic}
                onChange={(event) => setDraft({ ...draft, isPublic: event.target.checked })}
                className="size-4 accent-accent"
              />
              Aparece na vitrine e pode ser assinado — desmarque para fechar o plano sem tirar
              quem ja assina
            </label>

            <fieldset className="sm:col-span-2">
              <legend className="text-sm font-bold">Modulos</legend>
              <div className="mt-2 flex flex-wrap gap-2">
                {MODULE_CODES.map((code) => {
                  const on = code === 'core' || draft.modules.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      // `core` nao se desmarca: sem ele a conta loga e nao tem
                      // morador, unidade nem auditoria.
                      disabled={code === 'core'}
                      onClick={() =>
                        setDraft({
                          ...draft,
                          modules: on
                            ? draft.modules.filter((item) => item !== code)
                            : [...draft.modules, code],
                        })
                      }
                      className={cx(
                        'rounded-pill border px-3 py-1.5 text-xs font-semibold transition',
                        on
                          ? 'border-accent/50 bg-accent/10 text-accent'
                          : 'border-line bg-surface-2 text-muted',
                        code === 'core' && 'opacity-70',
                      )}
                    >
                      {MODULE_LABELS[code]}
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="sm:col-span-2">
              <legend className="text-sm font-bold">Limites</legend>
              <p className="mt-1 text-xs text-faint">
                Vazio = nao contratado (vale zero). Marque "sem limite" para ilimitado.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {LIMIT_KEYS.map((key) => {
                  const value = draft.limits[key];
                  const unlimited = value === null;
                  return (
                    <div key={key} className="grid gap-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted">
                        {LIMIT_LABELS[key]}
                      </span>
                      <div className="flex items-center gap-2">
                        <TextInput
                          value={unlimited || value === undefined ? '' : String(value)}
                          disabled={unlimited}
                          onChange={(event) => {
                            const raw = event.target.value.replace(/\D/g, '');
                            setDraft({
                              ...draft,
                              limits: {
                                ...draft.limits,
                                [key]: raw === '' ? undefined : Number(raw),
                              },
                            });
                          }}
                          inputMode="numeric"
                        />
                        <label className="flex shrink-0 items-center gap-1.5 text-xs text-muted">
                          <input
                            type="checkbox"
                            checked={unlimited}
                            onChange={(event) =>
                              setDraft({
                                ...draft,
                                limits: {
                                  ...draft.limits,
                                  [key]: event.target.checked ? null : undefined,
                                },
                              })
                            }
                            className="size-4 accent-accent"
                          />
                          sem limite
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </fieldset>

            <fieldset className="sm:col-span-2">
              <legend className="text-sm font-bold">Recursos</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {FEATURE_KEYS.map((key) => (
                  <label key={key} className="flex items-center gap-2 text-sm text-muted">
                    <input
                      type="checkbox"
                      checked={draft.features[key] === true}
                      onChange={(event) =>
                        setDraft({
                          ...draft,
                          features: { ...draft.features, [key]: event.target.checked },
                        })
                      }
                      className="size-4 accent-accent"
                    />
                    {FEATURE_LABELS[key]}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="flex flex-wrap gap-3 sm:col-span-2">
              <Button type="submit" loading={save.isPending} disabled={draft.name.trim().length < 2}>
                {criando ? 'Criar plano' : 'Salvar alteracoes'}
              </Button>
              <Button variant="ghost" type="button" onClick={close}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading && <LoadingRows rows={3} />}

      {/* Catalogo se compara lado a lado — empilhado, decidir se o Pro esta
          caro em relacao ao Essencial virava rolagem para cima e para baixo. */}
      <div className="mt-4 grid items-start gap-4 md:grid-cols-2 2xl:grid-cols-3">
        {items.map((plan) => (
          <Card key={plan.code} className={cx('p-5 sm:p-6', !plan.isPublic && 'border-warn/30')}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-xl font-bold tracking-tight">{plan.name}</h2>
                <p className="mt-1 text-sm text-muted">{plan.description}</p>
                <p className="mt-3 text-2xl font-extrabold tracking-tight tabular-nums">
                  {formatCurrency(plan.priceCents, 'BRL')}
                  <span className="text-sm font-semibold text-muted">
                    /{plan.billingPeriod === 'yearly' ? 'ano' : 'mes'}
                  </span>
                </p>
              </div>

              <div className="text-right">
                <StatusBadge tone={plan.subscribers > 0 ? 'accent' : 'neutral'}>
                  {plan.subscribers} {plan.subscribers === 1 ? 'conta' : 'contas'}
                </StatusBadge>
                {!plan.isPublic && (
                  <p className="mt-2 text-xs font-bold uppercase tracking-wider text-warn">
                    fechado
                  </p>
                )}
              </div>
            </div>

            <ul className="mt-5 flex flex-wrap gap-2 border-t border-line-soft pt-4">
              {plan.modules.map((code) => (
                <li
                  key={code}
                  className="rounded-pill border border-line bg-surface-2 px-3 py-1 text-xs font-semibold text-muted"
                >
                  {MODULE_LABELS[code as ModuleCode] ?? code}
                </li>
              ))}
            </ul>

            {!plan.isPublic && (
              <p className="mt-4 text-sm text-faint">
                Nao aparece na vitrine e nao pode ser pedido pelo sindico — vale so para quem ja
                assina.
              </p>
            )}

            {podeEditar && (
              <Button variant="quiet" className="mt-4 py-1.5" onClick={() => openEdit(plan)}>
                Editar
              </Button>
            )}
          </Card>
        ))}
      </div>
    </>
  );
}
